import React, { useState } from "react";
import { ArrowRight, MapPin, PawPrint, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Location from "../components/Location";
import InfoBanner from "../components/InfoBanner";
import BottomBanner from "../components/BottomBanner";
import Reveal from "../components/Reveal";
import image17 from "../assets/images/image17.jpg";
import homeBanner from "../assets/images/image17.jpg";
import mountains from "../assets/images/image17.jpg";
import murgab from "../assets/images/murgab.jpg";
import shurabad from "../assets/images/shurabad.jpg";
import nurek from "../assets/images/nurek.jpg";
import { locationKeys, locationImages } from "../assets/constants/locations";

const imageMap = { murgab, shurabad, nurek };

const Home = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();

  const territoryStats = [
    { value: "500,000", label: t("home.stats.hectares"), Icon: MapPin },
    { value: "7", label: t("home.stats.premierSpecies"), Icon: PawPrint },
    { value: "2018", label: t("home.stats.since"), Icon: Calendar },
  ];

  return (
    <div>
      <Header onOpenSidebar={() => setSidebarOpen(true)} />
      <Banner
        image={image17}
        title={t("home.bannerTitle")}
        onOpenSidebar={() => setSidebarOpen(true)}
      />

      <section className="py-16 sm:py-24 px-4 max-w-5xl mx-auto text-[#223746]">
        <Reveal direction="up" className="text-center mb-10">
          <p className="text-sm sm:text-base uppercase tracking-[0.3em] text-yellow-600 mb-4">
            {t("home.territory.kicker")}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            {t("home.territory.title")}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 text-sm sm:text-base leading-relaxed">
          <Reveal direction="left" delay={0.1}>
            <p>{t("home.territory.paragraph1")}</p>
          </Reveal>
          <Reveal direction="right" delay={0.2}>
            <p>{t("home.territory.paragraph2")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#f4f1ec] py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
            {territoryStats.map(({ value, label, Icon }, idx) => (
              <Reveal
                key={label}
                direction="up"
                delay={idx * 0.12}
                className="flex flex-col items-center text-center"
              >
                <Icon size={28} className="text-yellow-600 mb-3" />
                <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#223746]">
                  {value}
                </p>
                <p className="text-xs sm:text-sm uppercase tracking-widest text-[#223746]/70 mt-2">
                  {label}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal direction="up" delay={0.4} className="flex justify-center">
            <a
              href={`${process.env.PUBLIC_URL}/files/catalog_crd.pdf`}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-[#223746] px-6 py-3 text-sm font-semibold uppercase tracking-widest text-[#223746] hover:bg-[#223746] hover:text-white transition-colors duration-200"
            >
              {t("home.stats.discoverCta")}
              <ArrowRight
                size={18}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </a>
          </Reveal>
        </div>
      </section>

      <section
        style={{ backgroundImage: `url(${mountains})` }}
        className="relative h-[460px] sm:h-[560px] lg:h-[640px] bg-cover bg-center md:bg-fixed overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <Reveal
            direction="fade"
            duration={1.1}
            className="max-w-3xl text-center text-white"
          >
            <Reveal
              as="p"
              direction="up"
              delay={0.2}
              className="text-yellow-500 text-xs sm:text-sm uppercase tracking-[0.3em] mb-6"
            >
              {t("home.hero.kicker")}
            </Reveal>
            <Reveal
              as="p"
              direction="up"
              delay={0.4}
              className="text-2xl sm:text-3xl lg:text-5xl font-bold italic leading-tight"
            >
              {t("home.hero.quoteLine1")}
              <br />
              {t("home.hero.quoteLine2")}
            </Reveal>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 max-w-3xl mx-auto text-center text-[#223746]">
        <Reveal direction="up">
          <p className="text-base sm:text-lg leading-relaxed">
            {t("home.closing.intro")}
            <span className="font-semibold text-yellow-600">
              {t("home.closing.marcoPolo")}
            </span>
            {t("home.closing.afterMarcoPolo")}
            <span className="font-semibold text-yellow-600">
              {t("home.closing.markhor")}
            </span>
            {t("home.closing.afterMarkhor")}
            <span className="font-semibold text-yellow-600">
              {t("home.closing.ibex")}
            </span>
            {t("home.closing.afterIbex")}
          </p>
        </Reveal>
      </section>

      {locationKeys.map((key, i) => (
        <Location
          key={key}
          height={720}
          position={i % 2 === 0 ? "left" : "right"}
          image={imageMap[locationImages[key]]}
          fit={"contain"}
          title={t(`locations.${key}.title`)}
          subtitle={t(`locations.${key}.subTitle`)}
          description={t(`locations.${key}.description`)}
          subDescription={t(`locations.${key}.subDescription`, {
            defaultValue: "",
          })}
          location={t(`locations.${key}.location`)}
          attitude={t(`locations.${key}.attitude`)}
        />
      ))}

      <BottomBanner
        image={homeBanner}
        title={t("home.bottomBannerTitle")}
        shadow
      />
      <InfoBanner />
      <Footer />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </div>
  );
};

export default Home;
